import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, articleContents, categories } from '@/mocks/data';
import { ArticleSchema, BreadcrumbListSchema, WebPageSchema } from '@/components/base/SchemaOrg';

const categoryNames: Record<string, string> = {
  boundary: '母子边界模糊',
  dependency: '母子情感依赖',
  attachment: '依恋障碍',
  'mental-health': '亲子关系心理健康',
  intervention: '母子关系干预',
  counseling: '家庭心理咨询',
};

const articleDetailImages: Record<string, string> = {
  'article-kiss-01': 'https://readdy.ai/api/search-image?query=Warm%20minimalist%20psychology%20concept%20with%20soft%20natural%20lighting%2C%20gentle%20mother%20and%20child%20silhouette%2C%20emotional%20boundary%20theme%2C%20healing%20atmosphere%2C%20clean%20beige%20background%2C%20abstract%20wellness%20visual&width=1200&height=600&seq=detail-kiss-01&orientation=landscape',
  'article-boundary-02': 'https://readdy.ai/api/search-image?query=Balanced%20scales%20representing%20love%20and%20independence%20in%20family%20relationships%2C%20minimalist%20warm%20aesthetic%2C%20soft%20earth%20tones%2C%20clean%20composition%2C%20abstract%20boundary%20concept&width=1200&height=600&seq=detail-boundary-02&orientation=landscape',
  'article-touch-03': 'https://readdy.ai/api/search-image?query=Gentle%20physical%20touch%20boundary%20concept%20between%20mother%20and%20child%2C%20soft%20pastel%20warm%20tones%2C%20minimalist%20abstract%20illustration%2C%20clean%20healing%20atmosphere&width=1200&height=600&seq=detail-touch-03&orientation=landscape',
  'article-dependency-04': 'https://readdy.ai/api/search-image?query=Intertwined%20emotional%20connection%20visual%20with%20warm%20coral%20tones%2C%20abstract%20dependency%20concept%2C%20soft%20natural%20light%2C%20minimalist%20composition&width=1200&height=600&seq=detail-dependency-04&orientation=landscape',
  'article-enmesh-05': 'https://readdy.ai/api/search-image?query=Enmeshed%20family%20relationship%20abstract%20visual%2C%20overlapping%20shapes%20with%20warm%20terracotta%20and%20beige%2C%20minimalist%20design%2C%20healing%20psychology%20concept&width=1200&height=600&seq=detail-enmesh-05&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const content = useMemo(() => (slug ? articleContents[slug] : undefined), [slug]);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  if (!article || !content) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mx-auto mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">该文章可能已被移除或链接有误</p>
            <Link
              to="/category"
              className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
            >
              返回文章列表
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const baseUrl = 'https://www.maternal-psych-guide.cn';
  const articleUrl = `${baseUrl}/article/${article.slug}`;
  const imageUrl = articleDetailImages[article.imageSeq] || articleDetailImages['article-kiss-01'];

  return (
    <>
      <ScrollToTop />
      <ArticleSchema
        article={{
          title: article.title,
          excerpt: article.excerpt,
          author: article.author,
          date: article.date,
          url: articleUrl,
          imageUrl,
        }}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: baseUrl },
          { name: '文章分类', url: `${baseUrl}/category` },
          { name: categoryNames[article.category] || article.category, url: `${baseUrl}/category?tab=${article.category}` },
          { name: article.title, url: articleUrl },
        ]}
      />
      <WebPageSchema
        title={`${article.title} - 母子关系心理健康指南`}
        description={article.excerpt}
        url={articleUrl}
      />

      <Navbar />

      <main>
        {/* Article Header */}
        <article className="pt-24 pb-8 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '文章分类', path: '/category' },
                { label: categoryNames[article.category] || article.category, path: `/category?tab=${article.category}` },
                { label: article.title },
              ]}
            />

            <div className="mt-6 mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category?tab=${article.category}`}
                  className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium cursor-pointer"
                >
                  {categoryNames[article.category] || article.category}
                </Link>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
                <span className="text-xs text-foreground-400">|</span>
                <span className="text-xs text-foreground-400">{article.date}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-700 text-sm font-semibold">{article.author.charAt(0)}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-foreground-900">{article.author}</span>
                  <p className="text-xs text-foreground-400">持证心理咨询师</p>
                </div>
              </div>
            </div>

            <div className="w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden mb-10">
              <img
                src={imageUrl}
                alt={article.title}
                title={`${article.title} - 母子关系心理健康指南`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </article>

        {/* Article Content */}
        <article className="pb-14 md:pb-20 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="prose-custom">
              {content.sections.map((section, idx) => (
                <section key={idx} className="mb-10">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p key={pIdx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {para}
                    </p>
                  ))}
                  {section.listItems && section.listItems.length > 0 && (
                    <ul className="space-y-2 mb-4 pl-1">
                      {section.listItems.map((item, lIdx) => (
                        <li key={lIdx} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 flex-shrink-0"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              {content.conclusion && (
                <div className="mt-10 p-6 md:p-8 rounded-2xl bg-accent-50 border border-accent-100">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">结语</h3>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {content.conclusion}
                  </p>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}